import {ApiClient} from '@hconnect/apiclient'
import {EquipmentRunningTimesDto, Iso8601, PlantRunningTimesDto} from '@hconnect/common/types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {stringify} from 'query-string'
import {useEffect} from 'react'

import {selectSinglePlantEquipment} from '../../mappers'
import {QueryKey} from '../../types'

type Params = {
  plantId: string
  from?: Iso8601
  to?: Iso8601
}

type Options<TSelect> = Omit<
  UseQueryOptions<PlantRunningTimesDto[], AxiosError, TSelect>,
  'queryKey' | 'queryFn'
> & {
  onSuccess?: (data: TSelect) => void
}

export const usePlantRunningTimes = <TSelect = EquipmentRunningTimesDto[]>(
  {plantId, from, to}: Params,
  {onSuccess, ...queryOptions}: Options<TSelect> = {}
) => {
  const query = useQuery<PlantRunningTimesDto[], AxiosError, TSelect>({
    queryKey: [QueryKey.PlantRunningTimes, from, to, plantId],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<PlantRunningTimesDto[]>(
        '/runningtimes-orchestrator/running-times',
        {
          params: {from, to, plantIds: [plantId]},
          paramsSerializer: (params: string[]) => stringify(params, {arrayFormat: 'none'}),
          signal
        }
      )
      return response.data
    },
    select: selectSinglePlantEquipment as (data: PlantRunningTimesDto[]) => TSelect,
    ...queryOptions
  })

  useEffect(() => {
    if (query.isSuccess && onSuccess) {
      onSuccess(query.data)
    }
  }, [query.isSuccess, onSuccess, query.data])

  return query
}
